import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Minus, Search, Filter, Grid3X3, List, Download, Upload,
  Edit3, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  X, AlertTriangle, Check, DollarSign, Hash, Tag, Box, Camera, Save, Ban,
  RefreshCw, FileText, MoreHorizontal
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import Badge from '../components/common/Badge';
import Skeleton from '../components/common/Skeleton';
import ProductCard from '../components/inventory/ProductCard';
import ProductForm from '../components/inventory/ProductForm';
import LowStockAlert from '../components/inventory/LowStockAlert';
import StockMovementList from '../components/inventory/StockMovementList';
import {
  fetchProducts, createProduct, updateProduct, deleteProduct, uploadProductImage,
  fetchCategories, getLowStockAlerts, getMovements, addStock, removeStock, receiveStock,
  setPage, exportProducts
} from '../redux/slices/productSlice';
import {
  getEffectiveUnits, formatStockFromUnits, formatStockShortFromUnits,
  chainSummaryText,
} from '../utils/formatStock';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export default function Inventory() {
  const dispatch = useDispatch();
  const { products, loading, totalPages, currentPage, categories, alerts, movements } = useSelector(
    (state) => state.products
  );

  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockAction, setStockAction] = useState({ product: null, type: 'add' });
  const [stockQty, setStockQty] = useState('');
  const [stockUnitIndex, setStockUnitIndex] = useState('0');
  const [stockNote, setStockNote] = useState('');

  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveProduct, setReceiveProduct] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');
  const [receiveUnitIndex, setReceiveUnitIndex] = useState('0');
  const [receiveNote, setReceiveNote] = useState('');

  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 50 }));
    dispatch(fetchCategories());
    dispatch(getLowStockAlerts());
    dispatch(getMovements({ page: 1, limit: 20 }));
  }, [dispatch]);

  const handlePageChange = (page) => {
    dispatch(setPage(page));
    dispatch(fetchProducts({ page, limit: 50 }));
  };

  const filteredProducts = useMemo(() => {
    let result = products;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          (p.productName || p.name)?.toLowerCase().includes(q) ||
          (p.barcode || p.serialNumber)?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== 'All') {
      result = result.filter((p) => p.category === categoryFilter);
    }
    if (stockFilter === 'In Stock') {
      result = result.filter((p) => (p.stock || p.quantity || p.currentStock || 0) > 0);
    } else if (stockFilter === 'Low Stock') {
      result = result.filter(
        (p) => (p.stock || p.quantity || p.currentStock || 0) > 0 && (p.stock || p.quantity || p.currentStock || 0) <= (p.minimumStock ?? p.minStock ?? 5)
      );
    } else if (stockFilter === 'Out of Stock') {
      result = result.filter((p) => (p.stock || p.quantity || p.currentStock || 0) <= 0);
    }
    if (!showInactive) {
      result = result.filter((p) => p.active !== false);
    }
    return result;
  }, [products, searchQuery, categoryFilter, stockFilter, showInactive]);

  const handleCreateProduct = async (data, imageFile) => {
    try {
      const created = await dispatch(createProduct(data)).unwrap();
      const product = created?.product || created;
      const productId = product?._id || product?.productId;
      if (imageFile && productId) {
        await dispatch(uploadProductImage({ id: productId, file: imageFile })).unwrap();
      }
      setShowProductForm(false);
      toast.success('Product created successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to create product');
    }
  };

  const handleUpdateProduct = async (data, imageFile) => {
    try {
      const productId = editingProduct._id || editingProduct.productId || editingProduct.id;
      await dispatch(updateProduct({ id: productId, productData: data })).unwrap();
      if (imageFile) {
        await dispatch(uploadProductImage({ id: productId, file: imageFile })).unwrap();
      }
      setEditingProduct(null);
      setShowProductForm(false);
      toast.success('Product updated successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await dispatch(deleteProduct(deleteTarget._id || deleteTarget.id)).unwrap();
      setDeleteTarget(null);
      toast.success(res?.message || 'Product deleted');
    } catch (err) {
      toast.error(err?.message || err || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleStockAction = async () => {
    if (!stockAction.product || !stockQty) return;
    try {
      const qty = parseInt(stockQty, 10);
      const units = getEffectiveUnits(stockAction.product);
      const unit = units[Number(stockUnitIndex)]?.name;
      if (stockAction.type === 'add') {
        await dispatch(addStock({ id: stockAction.product._id || stockAction.product.id, quantity: qty, unit, note: stockNote })).unwrap();
        toast.success(`Added ${qty} ${unit || 'units'}`);
      } else {
        await dispatch(removeStock({ id: stockAction.product._id || stockAction.product.id, quantity: qty, unit, note: stockNote })).unwrap();
        toast.success(`Removed ${qty} ${unit || 'units'}`);
      }
      setShowStockModal(false);
      setStockQty('');
      setStockUnitIndex('0');
      setStockNote('');
    } catch (err) {
      toast.error(err?.message || 'Stock action failed');
    }
  };

  const handleReceiveStock = async () => {
    if (!receiveProduct) return;
    const qty = parseInt(receiveQty, 10);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    try {
      const units = getEffectiveUnits(receiveProduct);
      const unit = units[Number(receiveUnitIndex)]?.name;
      await dispatch(receiveStock({
        id: receiveProduct._id || receiveProduct.id,
        unit,
        quantity: qty,
        notes: receiveNote,
      })).unwrap();
      toast.success('Stock received');
      setShowReceiveModal(false);
      setReceiveProduct(null);
      setReceiveQty('');
      setReceiveUnitIndex('0');
      setReceiveNote('');
    } catch (err) {
      toast.error(err?.message || 'Failed to receive stock');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === filteredProducts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredProducts.map((p) => p._id || p.id));
    }
  };

  const handleExport = () => {
    dispatch(exportProducts({}));
    toast.success('Products exported');
  };

  const renderGridView = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      <AnimatePresence mode="popLayout">
        {filteredProducts.map((product, idx) => (
          <motion.div
            key={product._id || product.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ delay: idx * 0.02 }}
            className="relative"
          >
            {selectedIds.length > 0 && (
              <div className="absolute top-2 left-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(product._id || product.id)}
                  onChange={() => toggleSelect(product._id || product.id)}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                />
              </div>
            )}
            <ProductCard
              product={product}
              onEdit={(p) => {
                setEditingProduct(p);
                setShowProductForm(true);
              }}
              onDelete={(p) => setDeleteTarget(p)}
            />
            <div className="flex gap-1 mt-1">
              <button
                onClick={() => { setStockAction({ product, type: 'add' }); setShowStockModal(true); }}
                className="flex-[2] py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                + Add
              </button>
              <button
                onClick={() => { setReceiveProduct(product); setShowReceiveModal(true); }}
                className="flex-[2] py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                Receive
              </button>
              <button
                onClick={() => { setStockAction({ product, type: 'remove' }); setShowStockModal(true); }}
                className="flex-1 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
              >
                - Remove
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

  const renderListView = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/80">
              {selectedIds.length > 0 && (
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filteredProducts.length && filteredProducts.length > 0}
                    onChange={selectAll}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Product</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Price</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Stock</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            <AnimatePresence mode="popLayout">
              {filteredProducts.map((product, idx) => {
                const stock = product.stock || product.quantity || 0;
                const minStock = product.minStock || 5;
                const stockStatus = stock <= 0 ? 'danger' : stock <= minStock ? 'warning' : 'success';
                const statusLabel = stock <= 0 ? 'Out of Stock' : stock <= minStock ? 'Low Stock' : 'In Stock';
                return (
                  <motion.tr
                    key={product._id || product.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ delay: idx * 0.02 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    {selectedIds.length > 0 && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(product._id || product.id)}
                          onChange={() => toggleSelect(product._id || product.id)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          {(product.imageUrl || product.image) ? (
                            <img src={product.imageUrl || product.image} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Package size={18} className="text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</p>
                          {product.barcode && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">{product.barcode}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{product.category || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const priceUnits = getEffectiveUnits(product).filter((u) => u.sellable);
                        return (
                          <div className="space-y-0.5">
                            {priceUnits.map((u) => (
                              <p key={u.name} className="text-xs whitespace-nowrap">
                                <span className="text-gray-500 dark:text-gray-400">{u.name}: </span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  Rs. {(u.salePrice || 0).toLocaleString()}
                                </span>
                                {u.purchasePrice > 0 && (
                                  <span className="ml-1.5 text-[10px] text-gray-400 dark:text-gray-500 line-through">
                                    Rs. {(u.purchasePrice || 0).toLocaleString()}
                                  </span>
                                )}
                              </p>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-white" title={formatStockFromUnits(stock, getEffectiveUnits(product))}>
                        {formatStockShortFromUnits(stock, getEffectiveUnits(product))}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={stockStatus} size="sm" dot>{statusLabel}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setStockAction({ product, type: 'add' }); setShowStockModal(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                          title="Add Stock"
                        >
                          <Plus size={14} />
                        </button>
                        <button
                          onClick={() => { setReceiveProduct(product); setShowReceiveModal(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          title="Receive Stock (Boxes)"
                        >
                          <Box size={14} />
                        </button>
                        <button
                          onClick={() => { setStockAction({ product, type: 'remove' }); setShowStockModal(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Remove Stock"
                        >
                          <Minus size={14} />
                        </button>
                        <button
                          onClick={() => { setEditingProduct(product); setShowProductForm(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                          title="Edit"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(product)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <PageHeader
        title="Inventory Management"
        description="Manage your products and stock levels"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" icon={Download} onClick={handleExport}>
              Export
            </Button>
            <Button variant="secondary" size="sm" icon={Upload}>
              Import
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => { setEditingProduct(null); setShowProductForm(true); }}>
              Add Product
            </Button>
          </div>
        }
      />

      {alerts?.length > 0 && (
        <LowStockAlert
          alerts={alerts}
          onResolve={(alert) => {
            toast.success(`Alert resolved for ${alert.name}`);
          }}
        />
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, barcode, or SKU..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none focus:border-primary-500"
        >
          <option value="All">All Categories</option>
          {categories.map((cat) => (
            <option key={typeof cat === 'string' ? cat : cat._id || cat.id} value={typeof cat === 'string' ? cat : cat.name || cat}>
              {typeof cat === 'string' ? cat : cat.name || cat}
            </option>
          ))}
        </select>
        <select
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none focus:border-primary-500"
        >
          <option value="All">All Stock</option>
          <option value="In Stock">In Stock</option>
          <option value="Low Stock">Low Stock</option>
          <option value="Out of Stock">Out of Stock</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
          />
          Show Inactive
        </label>
        <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 ${viewMode === 'grid' ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
          >
            <Grid3X3 size={16} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 ${viewMode === 'list' ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' : ''}>
          {Array.from({ length: viewMode === 'grid' ? 10 : 5 }).map((_, i) => (
            viewMode === 'grid' ? (
              <Skeleton key={i} variant="card" />
            ) : (
              <Skeleton key={i} variant="table-row" className="mb-2" />
            )
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products found"
          description={searchQuery ? 'Try adjusting your search' : 'Add your first product to get started'}
          actionLabel="Add Product"
          onAction={() => { setEditingProduct(null); setShowProductForm(true); }}
        />
      ) : (
        <>
          {selectedIds.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 px-4 py-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl"
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {selectedIds.length} product(s) selected
              </span>
              <Button variant="ghost" size="sm" icon={Plus} onClick={() => {
                selectedIds.forEach((id) => {
                  const p = products.find((pr) => (pr._id || pr.id) === id);
                  if (p) {
                    setStockAction({ product: p, type: 'add' });
                    setShowStockModal(true);
                  }
                });
              }}>
                Add Stock
              </Button>
              <Button variant="ghost" size="sm" icon={Download} onClick={handleExport}>
                Export Selected
              </Button>
              <Button variant="ghost" size="sm" icon={X} onClick={() => setSelectedIds([])}>
                Clear
              </Button>
            </motion.div>
          )}
          {viewMode === 'grid' ? renderGridView() : renderListView()}
        </>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                page === currentPage
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Stock Movements</h3>
          <StockMovementList movements={movements} loading={loading} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Category Management</h3>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            {categories.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Badge key={typeof cat === 'string' ? cat : cat._id || cat.id} variant="primary" size="md">
                    {typeof cat === 'string' ? cat : cat.name || cat}
                  </Badge>
                ))}
              </div>
            ) : (
              <EmptyState title="No categories" description="Categories will appear here" />
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showProductForm}
        onClose={() => { setShowProductForm(false); setEditingProduct(null); }}
        size="xl"
        title={editingProduct ? 'Edit Product' : 'Add Product'}
      >
        <ProductForm
          product={editingProduct}
          onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
          onCancel={() => { setShowProductForm(false); setEditingProduct(null); }}
          loading={loading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message={`Permanently delete "${deleteTarget?.name}"? This removes the product from your system and cannot be undone. Products with sales history will only be deactivated.`}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />

      <Modal
        isOpen={showStockModal}
        onClose={() => { setShowStockModal(false); setStockQty(''); setStockNote(''); }}
        size="sm"
        title={`${stockAction.type === 'add' ? 'Add' : 'Remove'} Stock - ${stockAction.product?.name || ''}`}
      >
        <div className="space-y-4">
          {(() => {
            const units = getEffectiveUnits(stockAction.product || {});
            const unitIdx = Number(stockUnitIndex);
            const qty = parseInt(stockQty, 10) || 0;
            const base = qty * (units[unitIdx]?.baseFactor || 1);
            return (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Unit</label>
                    <select
                      value={stockUnitIndex}
                      onChange={(e) => setStockUnitIndex(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
                    >
                      {units.map((u, i) => (
                        <option key={i} value={String(i)}>
                          {u.name}{u.baseFactor > 1 ? ` (${u.baseFactor} ${units[0].name}s)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Quantity to {stockAction.type === 'add' ? 'add' : 'remove'}
                    </label>
                    <div className="relative">
                      <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        min="1"
                        value={stockQty}
                        onChange={(e) => setStockQty(e.target.value)}
                        placeholder="Enter quantity"
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
                {qty > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {qty} {units[unitIdx]?.name} = <span className="font-medium text-gray-700 dark:text-gray-200">{base.toLocaleString()} {units[0]?.name}s</span>
                  </p>
                )}
              </>
            );
          })()}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={stockNote}
              onChange={(e) => setStockNote(e.target.value)}
              placeholder="Reason for stock adjustment"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowStockModal(false); setStockQty(''); setStockUnitIndex('0'); setStockNote(''); }}>
              Cancel
            </Button>
            <Button
              variant={stockAction.type === 'add' ? 'success' : 'danger'}
              fullWidth
              onClick={handleStockAction}
              disabled={!stockQty || parseInt(stockQty) <= 0}
            >
              {stockAction.type === 'add' ? 'Add Stock' : 'Remove Stock'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showReceiveModal}
        onClose={() => { setShowReceiveModal(false); setReceiveProduct(null); setReceiveQty(''); setReceiveUnitIndex('0'); setReceiveNote(''); }}
        size="sm"
        title={`Receive Stock - ${receiveProduct?.name || ''}`}
      >
        <div className="space-y-4">
          {(() => {
            const units = getEffectiveUnits(receiveProduct || {});
            const chainLines = units.length > 1 ? chainSummaryText(units) : [];
            const unitIdx = Number(receiveUnitIndex);
            const qty = parseInt(receiveQty, 10) || 0;
            const base = qty * (units[unitIdx]?.baseFactor || 1);
            return (
              <>
                {chainLines.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300 space-y-0.5">
                    {chainLines.map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Unit</label>
                    <select
                      value={receiveUnitIndex}
                      onChange={(e) => setReceiveUnitIndex(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
                    >
                      {units.map((u, i) => (
                        <option key={i} value={String(i)}>
                          {u.name}{u.baseFactor > 1 ? ` (${u.baseFactor} ${units[0].name}s)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Quantity</label>
                    <div className="relative">
                      <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        min="1"
                        value={receiveQty}
                        onChange={(e) => setReceiveQty(e.target.value)}
                        placeholder="0"
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
                {qty > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {qty} {units[unitIdx]?.name} = <span className="font-medium text-gray-700 dark:text-gray-200">{base.toLocaleString()} {units[0]?.name}s</span>
                  </p>
                )}
              </>
            );
          })()}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={receiveNote}
              onChange={(e) => setReceiveNote(e.target.value)}
              placeholder="Supplier / invoice reference"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowReceiveModal(false); setReceiveProduct(null); setReceiveQty(''); setReceiveUnitIndex('0'); setReceiveNote(''); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleReceiveStock}
            >
              Receive Stock
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}


